import { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { matchingService } from '@/services/matching.service';
import type {
  ManualConsumptionRecord,
  ManualConsumptionUpsertRequest,
} from '@/types';
import { formatDateTime } from '@/utils/helpers';

export interface ManualConsumptionTarget {
  kind: 'match' | 'unmatched';
  model_id: number;
  match_id?: number;
  pbev_id?: number;
  fipe_id?: string;
  fipe_vehicle: string;
  pbev_vehicle?: string;
  confidence_score?: number;
  ano?: number;
  manual_consumption?: ManualConsumptionRecord | null;
}

interface ManualConsumptionDialogProps {
  open: boolean;
  target: ManualConsumptionTarget | null;
  onOpenChange: (open: boolean) => void;
  onSaved: (record: ManualConsumptionRecord) => Promise<void> | void;
}

type FormState = {
  combustivel: string;
  tipo_propulsao: string;
  consumo_alcool_cidade: string;
  consumo_alcool_estrada: string;
  consumo_gasolina_cidade: string;
  consumo_gasolina_estrada: string;
  consumo_hibrido_cidade: string;
  consumo_hibrido_estrada: string;
  consumo_energetico: string;
  autonomia_modo_eletrico: string;
  ano_referencia: string;
  notes: string;
};

const NUMERIC_FIELDS: Array<{
  key: keyof FormState;
  label: string;
  step?: string;
}> = [
  { key: 'consumo_gasolina_cidade', label: 'Gasoline city (km/l)' },
  { key: 'consumo_gasolina_estrada', label: 'Gasoline road (km/l)' },
  { key: 'consumo_alcool_cidade', label: 'Ethanol city (km/l)' },
  { key: 'consumo_alcool_estrada', label: 'Ethanol road (km/l)' },
  { key: 'consumo_hibrido_cidade', label: 'Hybrid or electric city (km/le)' },
  { key: 'consumo_hibrido_estrada', label: 'Hybrid or electric road (km/le)' },
  { key: 'consumo_energetico', label: 'Energy consumption (MJ/km)', step: '0.01' },
  { key: 'autonomia_modo_eletrico', label: 'Electric range (km)' },
  { key: 'ano_referencia', label: 'Reference year', step: '1' },
];

const buildFormState = (
  record: ManualConsumptionRecord | null | undefined,
  target: ManualConsumptionTarget | null
): FormState => ({
  combustivel: record?.combustivel ?? '',
  tipo_propulsao: record?.tipo_propulsao ?? '',
  consumo_alcool_cidade:
    record?.consumo_alcool_cidade !== undefined && record?.consumo_alcool_cidade !== null
      ? String(record.consumo_alcool_cidade)
      : '',
  consumo_alcool_estrada:
    record?.consumo_alcool_estrada !== undefined && record?.consumo_alcool_estrada !== null
      ? String(record.consumo_alcool_estrada)
      : '',
  consumo_gasolina_cidade:
    record?.consumo_gasolina_cidade !== undefined && record?.consumo_gasolina_cidade !== null
      ? String(record.consumo_gasolina_cidade)
      : '',
  consumo_gasolina_estrada:
    record?.consumo_gasolina_estrada !== undefined && record?.consumo_gasolina_estrada !== null
      ? String(record.consumo_gasolina_estrada)
      : '',
  consumo_hibrido_cidade:
    record?.consumo_hibrido_cidade !== undefined && record?.consumo_hibrido_cidade !== null
      ? String(record.consumo_hibrido_cidade)
      : '',
  consumo_hibrido_estrada:
    record?.consumo_hibrido_estrada !== undefined && record?.consumo_hibrido_estrada !== null
      ? String(record.consumo_hibrido_estrada)
      : '',
  consumo_energetico:
    record?.consumo_energetico !== undefined && record?.consumo_energetico !== null
      ? String(record.consumo_energetico)
      : '',
  autonomia_modo_eletrico:
    record?.autonomia_modo_eletrico !== undefined && record?.autonomia_modo_eletrico !== null
      ? String(record.autonomia_modo_eletrico)
      : '',
  ano_referencia:
    record?.ano_referencia !== undefined && record?.ano_referencia !== null
      ? String(record.ano_referencia)
      : target?.ano !== undefined && target?.ano !== null
        ? String(target.ano)
        : '',
  notes: record?.notes ?? '',
});

const parseOptionalNumber = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  const parsed = Number(trimmed.replace(',', '.'));
  return Number.isNaN(parsed) ? undefined : parsed;
};

const emptyToUndefined = (value: string): string | undefined => {
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
};

export function ManualConsumptionDialog({
  open,
  target,
  onOpenChange,
  onSaved,
}: ManualConsumptionDialogProps) {
  const [form, setForm] = useState<FormState>(buildFormState(null, null));
  const [existingRecord, setExistingRecord] = useState<ManualConsumptionRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !target) {
      return;
    }

    let active = true;
    setLoading(true);
    setError(null);

    matchingService
      .getManualConsumption(target.model_id)
      .then((record) => {
        if (!active) {
          return;
        }
        const resolvedRecord = record ?? target.manual_consumption ?? null;
        setExistingRecord(resolvedRecord);
        setForm(buildFormState(resolvedRecord, target));
      })
      .catch((requestError) => {
        if (!active) {
          return;
        }
        setError(
          requestError instanceof Error
            ? requestError.message
            : 'Failed to load manual consumption'
        );
        setExistingRecord(target.manual_consumption ?? null);
        setForm(buildFormState(target.manual_consumption, target));
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [open, target]);

  const updateField = (key: keyof FormState, value: string) => {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const handleSave = async () => {
    if (!target) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const payload: ManualConsumptionUpsertRequest = {
        match_id: target.match_id,
        pbev_id: target.pbev_id,
        combustivel: emptyToUndefined(form.combustivel),
        tipo_propulsao: emptyToUndefined(form.tipo_propulsao),
        consumo_alcool_cidade: parseOptionalNumber(form.consumo_alcool_cidade),
        consumo_alcool_estrada: parseOptionalNumber(form.consumo_alcool_estrada),
        consumo_gasolina_cidade: parseOptionalNumber(form.consumo_gasolina_cidade),
        consumo_gasolina_estrada: parseOptionalNumber(form.consumo_gasolina_estrada),
        consumo_hibrido_cidade: parseOptionalNumber(form.consumo_hibrido_cidade),
        consumo_hibrido_estrada: parseOptionalNumber(form.consumo_hibrido_estrada),
        consumo_energetico: parseOptionalNumber(form.consumo_energetico),
        autonomia_modo_eletrico: parseOptionalNumber(form.autonomia_modo_eletrico),
        ano_referencia: parseOptionalNumber(form.ano_referencia),
        notes: emptyToUndefined(form.notes),
        created_by: 'admin',
      };

      const savedRecord = await matchingService.saveManualConsumption(
        target.model_id,
        payload
      );
      await onSaved(savedRecord);
      onOpenChange(false);
    } catch (saveError) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : 'Failed to save manual consumption'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manual Consumption Override</DialogTitle>
          <DialogDescription>
            {target?.fipe_vehicle}
            {target?.pbev_vehicle ? ` • Candidate: ${target.pbev_vehicle}` : ''}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading current manual values…
          </div>
        ) : (
          <div className="space-y-6">
            <div className="rounded-lg border border-border/70 bg-muted/20 p-4">
              <div className="grid gap-2 text-sm md:grid-cols-2">
                <div>
                  <span className="text-muted-foreground">Target</span>
                  <div className="font-medium">{target?.fipe_vehicle}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Mode</span>
                  <div className="font-medium">
                    {target?.kind === 'match' ? 'Non-verified match' : 'Unmatched FIPE vehicle'}
                  </div>
                </div>
                {existingRecord && (
                  <>
                    <div>
                      <span className="text-muted-foreground">Current source</span>
                      <div className="font-medium">{existingRecord.source_type}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Last updated</span>
                      <div className="font-medium">{formatDateTime(existingRecord.updated_at)}</div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <div className="mb-2 text-sm font-medium text-muted-foreground">Fuel</div>
                <Input
                  value={form.combustivel}
                  onChange={(event) => updateField('combustivel', event.target.value)}
                  placeholder="Gasoline, flex, electric…"
                />
              </div>
              <div>
                <div className="mb-2 text-sm font-medium text-muted-foreground">Powertrain</div>
                <Input
                  value={form.tipo_propulsao}
                  onChange={(event) => updateField('tipo_propulsao', event.target.value)}
                  placeholder="Combustion, hybrid, BEV…"
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {NUMERIC_FIELDS.map((field) => (
                <div key={field.key}>
                  <div className="mb-2 text-sm font-medium text-muted-foreground">{field.label}</div>
                  <Input
                    type="number"
                    step={field.step ?? '0.1'}
                    value={form[field.key]}
                    onChange={(event) => updateField(field.key, event.target.value)}
                    placeholder="0.0"
                  />
                </div>
              ))}
            </div>

            <div>
              <div className="mb-2 text-sm font-medium text-muted-foreground">Notes</div>
              <Textarea
                value={form.notes}
                onChange={(event) => updateField('notes', event.target.value)}
                placeholder="Why this override exists, source caveats, vehicle-specific context…"
              />
            </div>

            {error && (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {error}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading || saving || !target}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save override
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}